/** Spinner, Skeleton, EmptyState, ErrorState — async/empty affordances (MASTER §8). */
import { Loader2, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from './cn.js';

export function Spinner({ className }: { className?: string }): ReactNode {
  return (
    <span role="status" aria-label="loading" className="inline-flex">
      <Loader2 className={cn('h-5 w-5 animate-spin text-muted', className)} aria-hidden />
    </span>
  );
}

export function CenterSpinner(): ReactNode {
  return (
    <div className="grid min-h-[40vh] place-items-center">
      <Spinner className="h-7 w-7 text-primary" />
    </div>
  );
}

/** A single shimmering block; pass width/height via className. */
export function Skeleton({ className }: { className?: string }): ReactNode {
  return <div className={cn('skeleton rounded-md', className)} aria-hidden />;
}

export function EmptyState({
  icon: Icon,
  title,
  hint,
  action,
  className,
}: {
  icon?: LucideIcon;
  title: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
  className?: string;
}): ReactNode {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border px-6 py-12 text-center',
        className,
      )}
    >
      {Icon ? (
        <span className="mb-1 grid h-12 w-12 place-items-center rounded-full bg-surface-2 text-muted">
          <Icon className="h-6 w-6" aria-hidden />
        </span>
      ) : null}
      <p className="font-display text-base font-semibold text-fg">{title}</p>
      {hint ? <p className="max-w-sm text-sm text-muted">{hint}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function ErrorState({
  title,
  detail,
  onRetry,
  retryLabel,
}: {
  title: ReactNode;
  detail?: ReactNode;
  onRetry?: () => void;
  retryLabel?: ReactNode;
}): ReactNode {
  return (
    <div className="flex flex-col items-center gap-2 rounded-xl border border-danger/40 bg-danger/5 px-6 py-10 text-center">
      <p className="font-display text-base font-semibold text-danger">{title}</p>
      {detail ? <p className="max-w-md text-sm text-muted">{detail}</p> : null}
      {onRetry ? (
        <button
          onClick={onRetry}
          className="mt-1 cursor-pointer rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-fg transition-colors hover:bg-surface-2"
        >
          {retryLabel ?? 'Retry'}
        </button>
      ) : null}
    </div>
  );
}
